import { createSelector } from 'reselect';

import { utils } from '../../../utils';
import { store } from '..';
import { SavedRestaurantVoucher } from './payment.types';

const checkRestaurantVouchersBalanceUnknown = createSelector(
  [
    (state: WebOrder.State, storeId: number) =>
      getRestaurantVouchers(state)[storeId],
  ],
  (restaurantVouchers) =>
    restaurantVouchers?.savedVouchers.some(
      (voucher) => voucher.AvailableBalance === null,
    ) ?? false,
);

const checkRestaurantVouchersCoverOrderAmount = createSelector(
  [
    (state: WebOrder.State, _orderTotalToPay: number, storeId: number) =>
      getRestaurantVouchers(state)[storeId],
    (_state: WebOrder.State, orderTotalToPay: number) => orderTotalToPay,
  ],
  (restaurantVouchers, orderTotalToPay) =>
    restaurantVouchers?.savedVouchers.some(
      (voucher) => voucher.AvailableBalance >= orderTotalToPay,
    ) ?? false,
);

const getAllAvailablePaymentsMethods = createSelector(
  [
    (state: WebOrder.State) => state,
    (state: WebOrder.State) => state.payments.items,
    (state: WebOrder.State) =>
      store.selectors.getRestaurantDetailsWrapper(state),
  ],
  (state, payments, details) => {
    if (!details) {
      return [];
    }

    const {
      AcceptCardOrders: acceptCardOrders,
      AcceptCashOrders: acceptCashOrders,
      AcceptRestaurantVoucherOrders: acceptRestaurantVoucherOrders,
    } = details;

    const paymentOptions = Object.values(payments)
      .filter((payment) => {
        const isPaymentOk =
          payment.status !== 'error' && payment.status !== 'none';
        const excludeCashPayment =
          !acceptCashOrders &&
          payment.PaymentAccountType === 'CashPaymentAccount';
        const excludeCardPayment =
          !acceptCardOrders &&
          payment.PaymentAccountType === 'CardPaymentAccount';

        return isPaymentOk && !excludeCashPayment && !excludeCardPayment;
      })
      .sort((a, b) => a.displayOrder - b.displayOrder);

    if (acceptRestaurantVoucherOrders) {
      const restaurantVouchers = getRestaurantVoucherPaymentOptions(state);
      return [...paymentOptions, ...restaurantVouchers];
    }

    return paymentOptions;
  },
);

const getPayment = (
  state: WebOrder.State,
  { paymentId }: { paymentId: string },
) => getPayments(state)[paymentId];

const getPayments = (state: WebOrder.State) => state.payments.items || {};

const getRemovablePayments = createSelector(
  [(state: WebOrder.State) => state.payments.items],
  (items) => {
    const removablePaymentMethods = Object.values(items).filter(
      (paymentMethod) => paymentMethod.CanRemoveFromCustomerView,
    );
    const sortedMethods = utils.change.sortBy(removablePaymentMethods);
    return sortedMethods;
  },
);

const getRestaurantVoucherPaymentOptions = createSelector(
  [
    (state: WebOrder.State) => state,
    (state: WebOrder.State) =>
      store.selectors.getRestaurantDetailsWrapper(state),
  ],
  (state, detail) => {
    if (!detail) {
      return [];
    }

    const { PhysicalRestaurantId: storeId } = detail;
    const savedVouchers = getRestaurantVouchers(state)[storeId]?.savedVouchers;

    let restaurantVoucherPaymentOptions: WebOrder.State.Payment[] = [];
    if (savedVouchers) {
      restaurantVoucherPaymentOptions = savedVouchers.map(
        (voucher: SavedRestaurantVoucher) => ({
          CanRemoveFromCustomerView: false,
          CustomerId: null,
          Description: voucher.Description,
          displayOrder: 0,
          IsDefaultPaymentMethod: false,
          PaymentAccountId: voucher.PaymentAccountId,
          PaymentAccountType: 'PayGreenRestaurantVoucher',
          paymentId: String(voucher.PaymentAccountId),
          PublicApiKey: '',
          status: 'success',
          SupportsSubmitOrderTips: false,
          TokenizationKey: '',
          UserId: 0,
        }),
      );
    }

    return restaurantVoucherPaymentOptions;
  },
);

const getRestaurantVouchers = (state: WebOrder.State) =>
  state.payments?.restaurantVouchers || {};

const getRestaurantVouchersForStore = createSelector(
  [
    (state: WebOrder.State, storeId: number) =>
      getRestaurantVouchers(state)[storeId],
  ],
  (restaurantVouchers) => restaurantVouchers,
);

const getSelectedPayment = (state: WebOrder.State) => {
  const paymentId = getSelectedPaymentId(state);

  if (paymentId) {
    const selectedPayment = state.payments.items[paymentId];
    if (selectedPayment) {
      return selectedPayment;
    }

    const selectedRestaurantVoucherPayment = getSelectedRestaurantVoucherPayment(
      state,
    );
    if (selectedRestaurantVoucherPayment) {
      return selectedRestaurantVoucherPayment;
    }
  }

  return undefined;
};

const getSelectedRestaurantVoucherPayment = createSelector(
  [
    (state: WebOrder.State) => getSelectedPaymentId(state),
    (state: WebOrder.State) => getRestaurantVoucherPaymentOptions(state),
  ],
  (paymentAccountId, paymentOptions) =>
    paymentOptions.find(
      (voucher) =>
        voucher.PaymentAccountId ===
        utils.change.stringToNumber(paymentAccountId),
    ),
);

const getSelectedPaymentId = (state: WebOrder.State) => state.basket.paymentId;

export const paymentSelectors = {
  checkRestaurantVouchersBalanceUnknown,
  checkRestaurantVouchersCoverOrderAmount,
  getAllAvailablePaymentsMethods,
  getPayment,
  getPayments,
  getRemovablePayments,
  getRestaurantVoucherPaymentOptions,
  getRestaurantVouchers,
  getRestaurantVouchersForStore,
  getSelectedPayment,
  getSelectedPaymentId,
  getSelectedRestaurantVoucherPayment,
};
